// client/src/pages/RecruitDetails.js
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import OpportunityGroupChat from "../components/OpportunityGroupChat";

const API = `${process.env.REACT_APP_API_URL}/api`;

export default function RecruitDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [showGroupChat, setShowGroupChat] = useState(false);

  const load = async () => {
    try {
      const res = await fetch(`${API}/opportunities/${id}`);
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || "Failed");
      setPost(d.item);
    } catch (err) {
      alert(err.message);
      navigate("/recruit");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [id]);

  const isOwner = user && String(user.id) === String(post?.postedBy);

  const alreadyApplied =
    user &&
    Array.isArray(post?.applicants) &&
    post.applicants.some((a) => String(a.userId) === String(user.id));

  const isSelected =
    user &&
    Array.isArray(post?.selected) &&
    post.selected.includes(String(user.id));

  // 🔒 SAME RULE AS BACKEND
  const canUseGroupChat = isOwner || isSelected;

  /* ---------------- APPLY ---------------- */
  const apply = async () => {
    if (!user) return navigate("/login");
    if (!message.trim()) return alert("Add a message");
    if (alreadyApplied) return;

    setSending(true);
    try {
      const token = localStorage.getItem("cp_token");
      const res = await fetch(`${API}/opportunities/${id}/apply`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ message }),
      });

      const d = await res.json();
      if (!res.ok) throw new Error(d.error);

      setMessage("");
      load();
    } catch (err) {
      alert(err.message);
    } finally {
      setSending(false);
    }
  };

  /* ---------------- OWNER ACTIONS ---------------- */

  // ✅ ACCEPT = SELECT (applicantId = USER ID)
  const acceptApplicant = async (userId) => {
    if (!isOwner) return;
    setActionLoading(true);
    try {
      const token = localStorage.getItem("cp_token");
      const res = await fetch(`${API}/opportunities/${id}/select`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ applicantId: userId }),
      });

      const d = await res.json();
      if (!res.ok) throw new Error(d.error || "Select failed");
      load();
    } catch (err) {
      alert(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  // ✅ REMOVE (after accepted)
  const removeApplicant = async (userId) => {
    if (!isOwner) return;
    if (!window.confirm("Remove this member?")) return;

    setActionLoading(true);
    try {
      const token = localStorage.getItem("cp_token");
      const res = await fetch(`${API}/opportunities/${id}/remove`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ applicantId: userId }),
      });

      const d = await res.json();
      if (!res.ok) throw new Error(d.error || "Remove failed");
      load();
    } catch (err) {
      alert(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) return <div className="p-6">Loading…</div>;
  if (!post) return <div className="p-6">Not found</div>;

  return (
    <div className="min-h-screen p-6 bg-slate-50 dark:bg-slate-900">
      <div className="max-w-3xl mx-auto bg-white dark:bg-slate-800 p-6 rounded-xl shadow">
        {/* Header */}
        <div className="flex justify-between items-center">
          <h1 className="text-xl font-bold dark:text-white">{post.title}</h1>
          <button onClick={() => navigate(-1)} className="px-3 py-1 border rounded dark:text-slate-200">
            Back
          </button>
        </div>

        {/* GROUP CHAT */}
        {canUseGroupChat && (
          <div className="mt-4">
            <button
              onClick={() => setShowGroupChat(true)}
              className="px-4 py-2 bg-indigo-600 text-white rounded"
            >
              Open Group Chat
            </button>
          </div>
        )}

        {/* IMAGE (UNCHANGED) */}
        {post.imageUrl && (
          <img
            src={post.imageUrl}
            alt="cover"
            className="w-full h-64 object-cover rounded-xl mt-4"
          />
        )}

        {/* DESCRIPTION */}
        <p className="mt-4 dark:text-gray-200">{post.description}</p>

        {/* APPLICANTS */}
        <h3 className="mt-6 font-semibold dark:text-white">Applicants</h3>

        {post.applicants?.length === 0 && (
          <p className="text-gray-500">No applicants yet.</p>
        )}

        <div className="space-y-3 mt-3">
          {post.applicants?.map((a) => {
            const selected = post.selected?.includes(String(a.userId));

            return (
              <div
                key={String(a.userId)}
                className="p-3 bg-slate-100 dark:bg-slate-900 rounded flex justify-between items-center"
              >
                <div>
                  <div className="font-medium dark:text-white">{a.name}</div>
                  <div className="text-sm dark:text-gray-400">{a.message}</div>

                  {selected && (
                    <span className="text-xs px-2 py-1 bg-green-200 text-green-700 rounded inline-block mt-2">
                      Selected ✔
                    </span>
                  )}
                </div>

                {/* OWNER CONTROLS */}
                {isOwner && (
                  <>
                    {!selected ? (
                      <button
                        onClick={() => acceptApplicant(a.userId)}
                        disabled={actionLoading}
                        className="px-3 py-1 bg-green-600 text-white rounded text-sm"
                      >
                        Accept
                      </button>
                    ) : (
                      <button
                        onClick={() => removeApplicant(a.userId)}
                        disabled={actionLoading}
                        className="px-3 py-1 bg-red-500 text-white rounded text-sm"
                      >
                        Remove
                      </button>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>

        {/* APPLY SECTION */}
        {!isOwner && !alreadyApplied && (
          <div className="mt-6">
            <h3 className="font-semibold dark:text-white">Apply</h3>

            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={3}
              className="w-full p-2 mt-2 rounded border dark:bg-slate-700 dark:border-gray-600 dark:text-white"
              placeholder="Short message to the organizer"
            />

            <button
              onClick={apply}
              disabled={sending}
              className="px-4 py-2 bg-indigo-600 text-white rounded mt-3"
            >
              {sending ? "Applying…" : "Apply"}
            </button>
          </div>
        )}

        {alreadyApplied && !isOwner && (
          <div className="mt-4 p-3 bg-blue-100 text-blue-700 rounded">
            You have already applied ✔
          </div>
        )}
      </div>

      {/* GROUP CHAT MODAL */}
      {showGroupChat && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center p-4 z-50">
          <div className="bg-white dark:bg-slate-800 rounded-xl p-4 w-full max-w-3xl shadow-2xl">
            <OpportunityGroupChat
              opportunityId={id}
              onClose={() => setShowGroupChat(false)}
            />
          </div>
        </div>
      )}
    </div>
  );
}
